// Professionals Domain Types
// Generated from DataModel.sql professionals schema
//
// RTK `professionals-api` contracts align with `Tempii.Application.Professionals.*` handlers
// (e.g. GetProfessional, GetProfessionalAvailability, ApplyToShift, GetProfessionalShifts).
// Cross-check payloads when adding endpoints.
import type {
  Address,
  BaseEntity,
  IntegerRange,
  Money,
  PaginatedResponse,
  RegisterUserRequest,
  UserDocument,
  User,
  UserType,
  VerificationStatus,
  StripeAccountStatus,
  WeekDay,
  PaginationParams
} from "./core";
import type { DeclaredPaymentStatus, ShiftEngagementPayment } from "./payments";
import type { Practice, PracticeBranch } from "./practices";
import {
  ActiveIcon,
  CheckBoldIcon,
  ClockIcon,
  CrossBoldIcon,
  UrgentIcon
} from "@/utilities/icon-utils";
import type { ShiftStatusColors } from "./shifts";

// Professional Entities
export interface SpecialtyCategory extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
}

export interface Specialty extends BaseEntity {
  tenantId: string;
  categoryId: string;
  title: string;
  description?: string;
}

export interface SkillCategory extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
}

export interface Certificate extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
}

export interface Skill extends BaseEntity {
  tenantId: string;
  categoryId: string;
  title: string;
  description?: string;
  certificateId?: string;
}

export interface Availability extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
  timeRangeStart: string;
  timeRangeEnd: string;
}

export interface ExperienceLevel extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
  fromYearsOfExperience: number;
  toYearsOfExperience: number;
}

export interface ProfessionalTier extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
  isDefault: boolean;
  requiredShiftHours: IntegerRange;
  /** Minimum average review rating, 0-5, to hold this tier. */
  requiredMinRating: number;
  tierBadgeSvgUrl?: string;
  priorityAccessValue?: number;
}

export interface ProfessionalTierReward extends BaseEntity {
  professionalTierId: string;
  title: string;
  description?: string;
  rewardType: ProfessionalTierRewardType;
  rewardValueAmount?: number;
  rewardValueCurrency?: string;
}

export type ProfessionalTierRewardType = "gift_card" | "voucher";

export interface MarketRate extends BaseEntity {
  tenantId: string;
  title: string;
  specialtyId: string;
  experienceLevelId: string;
  averageHourlyRate: Money;
  description?: string;
}

/**
 * Professional aggregate from `GET /professionals/{id}`.
 * Weekly availability rows are not embedded — load via `getProfessionalAvailability`
 * (`Tempii.Application.Professionals.GetProfessionalAvailability`).
 */
export interface Professional extends BaseEntity {
  tempiiId?: string;
  tenantId: string;
  stripeAccountId?: string;
  userId: string;
  user: User | null;
  licenseNumber?: string;
  yearsOfExperience?: number;
  bio?: string;
  hourlyRateMin?: Money;
  hourlyRateMax?: Money;
  maxTravelDistance: number;
  verificationStatus: VerificationStatus;
  verificationNotes?: string;
  verifiedAt?: Date;
  verifiedBy?: string;
  averageRating: number;
  totalShiftsCompleted: number;
  professionalTierId: string;
  professionalTier?: ProfessionalTier | null;
  referredByUserId?: string;
  address?: Address;
  profilePhotoId?: string;
  profilePhotoUrl?: string | null;
  specialties?: ProfessionalSpecialty[];
  skills?: ProfessionalSkill[];
}

export interface ProfessionalSpecialty extends BaseEntity {
  professionalId: string;
  specialtyId: string;
  yearsOfExperience?: number;
  specialty?: Specialty;
}

export interface ProfessionalAvailability extends BaseEntity {
  professionalId: string;
  availabilityId: string;
  dayOfWeek: WeekDay;
  availability?: Availability;
}

export interface ProfessionalCertificate extends BaseEntity {
  professionalId: string;
  documentId: string;
  document?: UserDocument | null;
  certificateId: string;
  certificate?: Certificate | null;
  certificateNumber?: string | null;
  creditedBy?: string | null;
  creditedByLink?: string | null;
  issuedDate?: Date | null;
  expiryDate?: Date | null;
}

export interface ProfessionalCertificateExtended
  extends ProfessionalCertificate {
  file?: File;
}

export interface ProfessionalSkill extends BaseEntity {
  professionalId: string;
  skillId: string;
  yearsOfExperience?: number;
  skill?: Skill;
}

// Request/Response Types
export interface CreateSpecialtyCategoryRequest {
  tenantId: string;
  title: string;
  description?: string;
}

export interface UpdateSpecialtyCategoryRequest
  extends Partial<CreateSpecialtyCategoryRequest> {
  id: string;
}

export interface CreateSpecialtyRequest {
  tenantId: string;
  categoryId: string;
  title: string;
  description?: string;
}

export interface UpdateSpecialtyRequest
  extends Partial<CreateSpecialtyRequest> {
  id: string;
}

export interface CreateSkillCategoryRequest {
  tenantId: string;
  title: string;
  description?: string;
}

export interface UpdateSkillCategoryRequest
  extends Partial<CreateSkillCategoryRequest> {
  id: string;
}

export interface CreateCertificateRequest {
  tenantId: string;
  title: string;
  description?: string;
}

export interface UpdateCertificateRequest
  extends Partial<CreateCertificateRequest> {
  id: string;
}

export interface CreateSkillRequest {
  tenantId: string;
  categoryId: string;
  title: string;
  description?: string;
  certificateId?: string;
}

export interface UpdateSkillRequest extends Partial<CreateSkillRequest> {
  id: string;
}

export interface CreateAvailabilityRequest {
  tenantId: string;
  title: string;
  description?: string;
  timeRangeStart: string;
  timeRangeEnd: string;
}

export interface UpdateAvailabilityRequest
  extends Partial<CreateAvailabilityRequest> {
  id: string;
}

export interface CreateExperienceLevelRequest {
  tenantId: string;
  title: string;
  description?: string;
  fromYearsOfExperience: number;
  toYearsOfExperience: number;
}

export interface UpdateExperienceLevelRequest
  extends Partial<CreateExperienceLevelRequest> {
  id: string;
}

export interface CreateProfessionalTierRequest {
  tenantId: string;
  title: string;
  description?: string;
}

export interface UpdateProfessionalTierRequest
  extends Partial<CreateProfessionalTierRequest> {
  id: string;
}

export interface CreateMarketRateRequest {
  tenantId: string;
  title: string;
  specialtyId: string;
  experienceLevelId: string;
  averageHourlyRate: Money;
  description?: string;
}

export interface UpdateMarketRateRequest
  extends Partial<CreateMarketRateRequest> {
  id: string;
}

/** @see Tempii.Application.Professionals.AddProfessionalSpecialty.AddProfessionalSpecialtyRequest */
export interface CreateProfessionalSpecialtyRequest {
  professionalId: string;
  specialtyId: string;
  yearsOfExperience?: number;
}

export interface UpdateProfessionalSpecialtyRequest
  extends Partial<CreateProfessionalSpecialtyRequest> {
  id: string;
}

export interface CreateProfessionalCertificateRequest {
  professionalId: string;
  documentId: string;
  certificateId: string;
  certificateNumber?: string;
  creditedBy?: string;
  creditedByLink?: string;
  issuedDate?: Date;
  expiryDate?: Date;
}

export interface UpdateProfessionalCertificateRequest
  extends Partial<CreateProfessionalCertificateRequest> {
  professionalId: string;
  certificateId: string;
}

export interface UpdateProfessionalCertificateResponse {
  value: string;
}

/** @see Tempii.Application.Professionals.AddProfessionalSkill.AddProfessionalSkillRequest */
export interface CreateProfessionalSkillRequest {
  professionalId: string;
  skillId: string;
  yearsOfExperience?: number;
}

export interface UpdateProfessionalSkillRequest
  extends Partial<CreateProfessionalSkillRequest> {
  id: string;
}

export interface SpecialtyWithCategory extends Specialty {
  category?: SpecialtyCategory;
}

export interface SkillWithCategory extends Skill {
  category?: SkillCategory;
  certificate?: Certificate;
}

export interface MarketRateWithDetails extends MarketRate {
  specialty?: Specialty;
  experienceLevel?: ExperienceLevel;
}

// API Request & Response Types
// RTK `professionals-api` — mirror `Tempii.Application.Professionals.*` handlers.

// Professional Statistics API
/** @see Tempii.Application.Professionals.GetProfessionalStats.GetProfessionalStatsQuery */
export interface GetProfessionalStatsRequest {
  professionalId: string;
  dateRange?: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalStats.GetProfessionalStatsResponse */
export interface GetProfessionalStatsResponse {
  totalShifts: number;
  completedShifts: number;
  upcomingShifts: number;
  totalEarnings: number;
  averageRating: number;
  totalReviews: number;
  availabilityHours: 0;
  specializations: string[];
  certifications: string[];
  performanceMetrics: {
    onTimeArrival: number;
    patientSatisfaction: number;
    documentationCompletion: number;
    teamCollaboration: 0;
  };
  monthlyTrends: {
    shiftsCompleted: string[];
    earnings: [];
    ratings: string[];
  };
  recentActivity: string[];
}

// Get Specific Professional API
export type GetProfessionalRequest = { id: string };
/** @see Tempii.Application.Professionals.GetProfessional (payload: professional aggregate) */
export type GetProfessionalResponse = Professional;

// Update Professional API
/** @see Tempii.Application.Professionals.UpdateProfessionalProfile.UpdateProfessionalProfileRequest */
export interface UpdateProfessionalRequest {
  // User information
  userId: string;
  namePrefix: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  phone: string;
  email?: string;
  designation: string;

  // Professional information
  id: string;
  licenseNumber?: string;
  yearsOfExperience?: number;
  bio?: string;
  hourlyRateMin?: Money | null;
  hourlyRateMax?: Money | null;
  maxTravelDistance: number;
  address?: Address | null;
}

export type UpdateProfessionalResponse = Professional;

// Professional Availability API — RTK: `professionals-api` get/add/remove availability
/** @see Tempii.Application.Professionals.GetProfessionalAvailability.GetProfessionalAvailabilityQuery */
export type GetProfessionalAvailabilityRequest = string; // professional id
/** @see Tempii.Application.Professionals.GetProfessionalAvailability (payload: `IEnumerable<ProfessionalAvailabilityPayload>`) */
export type GetProfessionalAvailabilityResponse = ProfessionalAvailability[];

/** @see Tempii.Application.Professionals.AddProfessionalAvailability.AddProfessionalAvailabilityRequest */
export interface AddProfessionalAvailabilityRequest {
  professionalId: string;
  availabilityId: string;
  dayOfWeek: WeekDay;
}

/** @see Tempii.Application.Professionals.RemoveProfessionalAvailability.RemoveProfessionalAvailabilityRequest */
export interface RemoveProfessionalAvailabilityRequest {
  professionalId: string;
  availabilityId: string;
  dayOfWeek: WeekDay;
}

export type SpecialityRecord = {
  specialtyId: string;
  yearsOfExperience: number;
};

export type SkillRecord = {
  skillId: string;
  yearsOfExperience?: number;
};

/** @see Tempii.Application.Professionals.RegisterProfessional.RegisterProfessionalRequest */
export interface RegisterProfessionalRequest extends RegisterUserRequest {
  bio?: string;
  hourlyRateMin?: Money;
  hourlyRateMax?: Money;
  maxTravelDistance?: number;
  licenseNumber?: string;
  yearsOfExperience?: string;
  specialties?: SpecialityRecord[];
  skills?: SkillRecord[];
  /** Referral code redeemed at sign-up, from a `?ref=` link or typed in. Optional. */
  referralCode?: string;
}

export interface ProfessionalRegistrationData
  extends Omit<RegisterProfessionalRequest, "userType"> { }

/** @see Tempii.Application.Professionals.RegisterProfessional (registration result) */
export interface RegisterProfessionalResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  userType: UserType;
  professionalId: string;
  bio: string;
  hourlyRateMin: Money;
  hourlyRateMax: Money;
  maxTravelDistance: number;
  verificationStatus: number;
}

/** @see Tempii.Application.Professionals.UploadProfessionalDocument.UploadProfessionalDocumentCommand */
export interface UploadProfessionalDocumentRequest {
  professionalId: string;
  formData: FormData;
}

export interface UploadProfessionalDocumentResponse extends UserDocument { }

/** @see Tempii.Application.Professionals.UpdateProfessionalDocument.UpdateProfessionalDocumentCommand */
export interface UpdateProfessionalDocumentRequest {
  professionalId: string;
  documentId: string;
  description: string;
}

export type UpdateProfessionalDocumentResponse = {
  value: string;
};

/** @see Tempii.Application.Professionals.DeleteProfessionalDocument.DeleteProfessionalDocumentCommand */
export interface DeleteProfessionalDocumentRequest {
  professionalId: string;
  documentId: string;
}

export type DeleteProfessionalDocumentResponse = {
  value: string;
};

/** @see Tempii.Application.Professionals.DownloadProfessionalDocument */
export interface DownloadProfessionalDocumentRequest {
  professionalId: string;
  documentId: string;
}

export type DownloadProfessionalDocumentResponse = Blob;

/** @see Tempii.Application.Professionals.GetProfessionalLookups.GetProfessionalLookupsQuery */
export interface GetProfessionalRegistrationLookupsRequest { }

/** @see Tempii.Application.Professionals.GetProfessionalLookups.GetProfessionalLookupsResponse */
export interface GetProfessionalRegistrationLookupsResponse {
  specialtyCategories: SpecialtyCategory[];
  specialties: Specialty[];
  skillCategories: SkillCategory[];
  skills: Skill[];
  professionalTiers: ProfessionalTier[];
  experienceLevels: ExperienceLevel[];
  certificates: Certificate[];
  availabilities: Availability[];
}

/** @see Tempii.Application.Professionals.GetProfessionalDocuments.GetProfessionalDocumentsResponse */
export interface GetProfessionalDocumentsResponse {
  documents: UserDocument[];
}

/** @see Tempii.Application.Professionals.GetProfessionalDocuments.GetProfessionalDocumentsQuery */
export interface GetProfessionalDocumentsRequest {
  professionalId: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalCertificates.GetProfessionalCertificatesQuery */
export interface GetProfessionalCertificatesRequest {
  professionalId: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalCertificates.GetProfessionalCertificatesResponse */
export interface GetProfessionalCertificatesResponse {
  certificates: ProfessionalCertificate[];
}

/** @see Tempii.Application.Professionals.DeleteProfessionalCertificate.DeleteProfessionalCertificateCommand */
export interface DeleteProfessionalCertificateRequest {
  professionalId: string;
  certificateId: string;
}

export type DeleteProfessionalCertificateResponse = {
  value: string;
};

/** @see Tempii.Application.Professionals.UploadProfessionalCertificate.UploadProfessionalCertificateCommand */
export interface UploadProfessionalCertificateRequest {
  professionalId: string;
  formData: FormData;
}

export interface UploadProfessionalCertificateResponse {
  value: string;
}

// ============================================================================
// Professional Shift API Types
// Aligned with backend Tempii.Application.Modules.Professionals endpoints
// ============================================================================
//
// Shift vs ShiftPost vs ShiftApplication (matches backend domain):
// - shiftId → shifts.shifts (one logical shift; apply / withdraw / opportunity URL use this).
// - applicationId → shifts.shift_applications (one row per professional per shift).
// - shiftPostId → shifts.shift_posts (one dated instance). List DTOs and opportunity still expose a
//   shiftPostId for the row used to show date/time/title/rates (earliest post or the search row’s post).
//   It is not the key for applying; use shiftId for routes and ApplyToShift.
//
// ============================================================================

/**
 * Application status from the professional's perspective.
 * Mirrors ApplicationStatus from shifts domain to avoid circular imports.
 */
export type ProfessionalApplicationStatus =
  | "draft"
  | "applied"
  | "nominated"
  | "not_nominated"
  | "withdrawn"
  | "selected";

/** Query-only temporal filter for shift date disambiguation. */
export type ShiftTimeframe = "past" | "today" | "future";

export interface GetProfessionalShiftsFilters extends PaginationParams {
  professionalId: string;
  startDate?: string;
  endDate?: string;
  statuses?: ProfessionalApplicationStatus[];
  specialties?: string[];
  practiceIds?: string[];
  minHourlyRate?: number;
  maxHourlyRate?: number;
  timeframe?: ShiftTimeframe;
}

// GET /professionals/{id}/shifts
/** @see Tempii.Application.Professionals.GetProfessionalShifts.GetProfessionalShiftsQuery */
export type GetProfessionalShiftsRequest = GetProfessionalShiftsFilters;

/** Row in professional shifts list — mirrors GetProfessionalShifts response item DTO. */
export interface GetProfessionalShiftsResponseItem {
  /** shifts.shift_applications.id */
  applicationId: string;
  /** shifts.shifts.id */
  shiftId: string;
  /** Display instance: shifts.shift_posts.id (earliest post on the shift for this row). */
  shiftPostId: string;
  professionalId: string;
  title: string;
  practice: Practice;
  practiceBranch: PracticeBranch;
  status: ProfessionalApplicationStatus;
  shiftDate: string;
  startTime: string;
  endTime: string;
  location: Address | null;
  hourlyRate: Money | null;
  notes: string | null;
  appliedAt?: string | null;
  /** Real attendance, from the shift_application_post for the date being worked. */
  isCheckedIn?: boolean | null;
  checkedInAt?: string | null;
  /** Off-platform payment state for the earliest date. */
  paymentStatus?: DeclaredPaymentStatus | null;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  confirmedAt?: string | null;
}

// GET /professionals/{id}/shifts/applied
/** @see Tempii.Application.Professionals.GetAppliedShifts.GetAppliedShiftsQuery */
export type GetAppliedShiftsRequest = GetProfessionalShiftsFilters;

/** @see Tempii.Application.Professionals.GetAppliedShifts (row DTO) */
export interface GetAppliedShiftsResponseItem {
  applicationId: string;
  shiftId: string;
  /** Display instance (earliest post) for date/title; application remains shift-scoped. */
  shiftPostId: string;
  title: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  practice: Practice;
  practiceBranch: PracticeBranch;
  hourlyRate: Money;
  shiftStatus: string;
  applicationStatus: ProfessionalApplicationStatus;
  appliedAt: string;
  notes: string | null;
  /** Real attendance, from the shift_application_post for the date being worked. */
  isCheckedIn?: boolean | null;
  checkedInAt?: string | null;
  /** Off-platform payment state for the earliest date. */
  paymentStatus?: DeclaredPaymentStatus | null;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  confirmedAt?: string | null;
}

// GET /professionals/{id}/shifts/upcoming
/** @see Tempii.Application.Professionals.GetProfessionalUpcomingShifts.GetProfessionalUpcomingShiftsQuery */
export type GetProfessionalUpcomingShiftsRequest = GetProfessionalShiftsFilters;

/** @see Tempii.Application.Professionals.GetProfessionalUpcomingShifts (row DTO) */
export interface GetProfessionalUpcomingShiftsResponseItem {
  applicationId: string;
  shiftId: string;
  /** Display instance (earliest post) for date/title. */
  shiftPostId: string;
  title: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  practice: Practice;
  practiceBranch: PracticeBranch;
  hourlyRate: Money | null;
  status: string;
  applicationStatus: ProfessionalApplicationStatus;
  appliedAt?: string | null;
  /** Real attendance, from the shift_application_post for the date being worked. */
  isCheckedIn?: boolean | null;
  checkedInAt?: string | null;
  /** Off-platform payment state for the earliest date. */
  paymentStatus?: DeclaredPaymentStatus | null;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  confirmedAt?: string | null;
}

// GET /professionals/{id}/shifts/{shiftId}
/** @see Tempii.Application.Professionals.GetProfessionalShift.GetProfessionalShiftQuery */
export interface GetProfessionalShiftRequest {
  professionalId: string;
  shiftId: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalShift.GetProfessionalShiftResponse */
export interface GetProfessionalShiftDetailResponse {
  id: string;
  title: string;
  description: string;
  specialtyName: string;
  shiftTimeType: string;
  recurringDateStart: string | null;
  recurringDateEnd: string | null;
  shiftPricingType: string;
  hourlyRate: Money | null;
  totalValue: Money;
  specialRequirements: string | null;
  dressCode: string | null;
  parkingInfo: string | null;
  status: string;
  completedAt: string | null;
  practiceName: string;
  practiceBranchName: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalShiftOpportunity.GetProfessionalShiftOpportunityQuery */
export interface GetProfessionalShiftOpportunityRequest {
  professionalId: string;
  shiftId: string;
}

/** Mirrors Tempii `ShiftOpportunitySkillItem`; keep aligned with API (Phase E). */
export interface ShiftOpportunitySkillItem {
  skillId: string;
  title: string;
  isRequired: boolean;
  yearsOfExperience?: number | null;
  notes?: string | null;
}

/** Mirrors Tempii `ShiftOpportunityDocumentItem`; prefer `cdnUri` then `fileUri` for links. */
export interface ShiftOpportunityDocumentItem {
  documentId: string;
  documentType: string;
  fileName: string;
  description?: string | null;
  /** Absolute http(s) URL when the API could parse the stored file URL. */
  fileUri?: string | null;
  /** CDN URL when available (preferred for download/open). */
  cdnUri?: string | null;
}

/**
 * Mirrors `GetProfessionalShiftOpportunityResponse` (Tempii.Application).
 * Resource key is the shift; apply/withdraw use `shiftId` only.
 */
export interface GetProfessionalShiftOpportunityResponse {
  /** shifts.shifts.id — primary id for this view and for ApplyToShift. */
  shiftId: string;
  /** shifts.shift_applications.id when the professional has applied; null/omit if not. */
  applicationId?: string | null;
  /**
   * JSON name `shiftPostId` (from API): id of the **earliest** `shifts.shift_posts` row used for
   * title, date, times, rates, and post status string in this payload. Not used for routing.
   */
  shiftPostId: string;
  title: string;
  description?: string | null;
  shiftDate: string;
  startTime: string;
  endTime: string;
  totalHours: number;
  hourlyRateAmount?: number | null;
  hourlyRateCurrency?: string | null;
  totalValueAmount: number;
  totalValueCurrency: string;
  /** Post status of the representative `shiftPostId` row (not necessarily `shifts.shifts.status`). */
  shiftStatus: string;
  /** Shift-level aggregate from `shifts.shifts`. */
  candidatesCount: number;
  /** Shift-level aggregate from `shifts.shifts`. */
  candidatesFulfilled: number;
  /** practices.practices.id — the address a rating is sent to. */
  practiceId: string;
  practiceName: string;
  practiceLogo?: string | null;
  practiceBranchName: string;
  practiceAddress?: Address | null;
  specialtyName?: string | null;
  applicationStatus?: string | null;
  appliedAt?: string | null;
  selectedAt?: string | null;
  /** ISO datetime when the professional accepted the nomination; omit if not nominated or not yet accepted. */
  nominationAcceptedAt?: string | null;
  canApply: boolean;
  /** Attendance and payment state for the earliest date, once approved. */
  applicationPostStatus?: string | null;
  checkedInAt?: string | null;
  completedAt?: string | null;
  paymentStatus?: DeclaredPaymentStatus | null;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  confirmedAt?: string | null;
  /** True when a Rate control here will be accepted rather than refused. */
  canRate: boolean;
  /** This professional's own rating of the engagement, once they have left one. */
  myRatingId?: string | null;
  /** Post overrides shift when set. */
  specialRequirements?: string | null;
  dressCode?: string | null;
  parkingInfo?: string | null;
  /** Required/preferred skills from the shift definition (omit on older API responses). */
  requiredSkills?: ShiftOpportunitySkillItem[];
  /** Practice reference documents — metadata only (omit on older API responses). */
  referenceDocuments?: ShiftOpportunityDocumentItem[];
}

// POST /professionals/{id}/shifts/{shiftId}/apply
/** @see Tempii.Application.Professionals.ApplyToShift.ApplyToShiftCommand */
export interface ApplyToShiftApiRequest {
  professionalId: string;
  shiftId: string;
  hourlyRate: Money;
  notes?: string;
}

/** Apply mutation returns shift application id wrapper. */
export interface ApplyToShiftResponse {
  value: string;
}

// GET /professionals/{id}/quick-stats
/** @see Tempii.Application.Professionals.GetProfessionalQuickStats.GetProfessionalQuickStatsQuery */
export interface GetProfessionalQuickStatsRequest {
  professionalId: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalQuickStats.GetProfessionalQuickStatsResponse */
export interface GetProfessionalQuickStatsResponse {
  totalShifts: number;
  completedShifts: number;
  upcomingShifts: number;
  pendingApplications: number;
  totalEarnings: number;
  averageRating: number;
  totalReviews: number;
  /** Sum of payments confirmed received in the current UTC month. */
  monthlyEarnings?: number;
}

// GET /professionals/{id}/calendar-stats
/** @see Tempii.Application.Professionals.GetProfessionalCalendarStats.GetProfessionalCalendarStatsQuery */
export interface GetProfessionalCalendarStatsRequest {
  professionalId: string;
  startDate: string;
  endDate: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalCalendarStats.GetProfessionalCalendarStatsResponse */
export interface GetProfessionalCalendarStatsResponse {
  totalShifts: number;
  completedShifts: number;
  pendingShifts: number;
  cancelledShifts: number;
  totalHours: number;
  totalEarnings: number;
  shiftsByDate: Record<string, number>;
  shiftsByPractice: Record<string, number>;
}

// GET /professionals/{id}/tier-progress
/** @see Tempii.Application.Professionals.GetProfessionalTierProgress.GetProfessionalTierProgressQuery */
export interface GetProfessionalTierProgressRequest {
  professionalId: string;
}

export interface ProfessionalTierRewardPayload {
  id: string;
  professionalTierId: string;
  title: string;
  description?: string;
  rewardType: ProfessionalTierRewardType;
  rewardValueAmount?: number;
  rewardValueCurrency?: string;
}

/** @see Tempii.Application.Professionals.GetProfessionalTierProgress.GetProfessionalTierProgressResponse */
export interface GetProfessionalTierProgressResponse {
  currentTier: ProfessionalTier;
  nextTier?: ProfessionalTier | null;
  currentTierRewards: ProfessionalTierRewardPayload[];
  nextTierRewards: ProfessionalTierRewardPayload[];
  completedHoursThisMonth: number;
  maintenanceHoursRemaining: number;
  nextTierHoursRemaining?: number | null;
  progressPercentage: number;
  daysUntilReset: number;
  completedShiftsThisMonth?: number;
  estimatedShiftsRemainingForNextTier?: number | null;
  /** Averaged from the reviews themselves, not the stale professionals.average_rating column. */
  averageRating: number;
  currentTierRequiredRating: number;
  nextTierRequiredRating?: number | null;
  /** A tier asks for hours *and* a rating; hitting the hours alone is not qualifying. */
  meetsCurrentTierRating: boolean;
  meetsNextTierRating: boolean;
}

// GET /professionals/{id}/referrals
/** @see Tempii.Application.Professionals.GetProfessionalReferrals.GetProfessionalReferralsQuery */
export interface GetProfessionalReferralsRequest {
  professionalId: string;
  page?: number;
  pageSize?: number;
}

/** @see Tempii.Shared.ReferralStatus */
export type ReferralStatus = "active" | "completed" | "cancelled";

/** Display labels for referral list rows and dashboard stats. */
export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  active: "In progress",
  completed: "Complete",
  cancelled: "Cancelled"
};

/** @see Tempii.Shared.ReferralMilestoneType */
export type ReferralMilestoneType =
  | "registered"
  | "verified"
  | "first_application"
  | "first_shift_completed";

/** @see Tempii.Application.Professionals.GetProfessionalReferrals.ReferralMilestoneState */
export interface ReferralMilestoneStateDto {
  milestone: ReferralMilestoneType;
  label: string;
  achieved: boolean;
  achievedAt?: string | null;
  /** When the reward engine granted a reward for this step; null while unprocessed. */
  rewardedAt?: string | null;
}

/** @see Tempii.Application.Professionals.GetProfessionalReferrals.ProfessionalReferralListItem */
export interface ProfessionalReferralListItemDto {
  id: string;
  displayName: string;
  status: ReferralStatus;
  progressText: string;
  milestonesAchieved: number;
  milestonesTotal: number;
  joinedAt: string;
  completedAt?: string | null;
  /** Every step of the chain, achieved or not, in order. */
  milestones: ReferralMilestoneStateDto[];
}

/** @see Tempii.Application.Professionals.GetProfessionalReferrals.GetProfessionalReferralsResponse */
export interface GetProfessionalReferralsResponse {
  /** The professional's own share code, for the invite link. */
  referralCode: string;
  /** Counts span every page, not just the one returned. */
  totalCount: number;
  activeCount: number;
  completedCount: number;
  items: ProfessionalReferralListItemDto[];
  pagination: PaginatedResponse<ProfessionalReferralListItemDto>["pagination"];
}

// GET /professionals/{id}/payments/overview
/** @see Tempii.Application.Payments.GetProfessionalPaymentsOverview.GetProfessionalPaymentsOverviewQuery */
export interface GetProfessionalPaymentsOverviewRequest {
  professionalId: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  searchTerm?: string;
}

export interface ProfessionalPaymentStats {
  confirmedEarnings: number;
  thisMonthConfirmedEarnings: number;
  awaitingConfirmation: number;
  pendingCount: number;
  declaredCount: number;
  confirmedCount: number;
  disputedCount: number;
}

/** @see Tempii.Application.Payments.GetProfessionalPaymentsOverview.GetProfessionalPaymentsOverviewResponse */
export interface GetProfessionalPaymentsOverviewResponse {
  stats: ProfessionalPaymentStats;
  payments: PaginatedResponse<ShiftEngagementPayment>;
}

// POST /professionals/{id}/shifts/{shiftId}/withdraw
/** @see Tempii.Application.Professionals.WithdrawShiftApplication.WithdrawShiftApplicationCommand */
export interface WithdrawShiftApplicationRequest {
  professionalId: string;
  shiftId: string;
}

export interface WithdrawShiftApplicationResponse {
  value: string;
}

// POST /professionals/{id}/shifts/{shiftId}/accept-nomination
/** @see Tempii.Application.Shifts.AcceptShiftNomination.AcceptShiftNominationCommand */
export interface AcceptShiftNominationRequest {
  professionalId: string;
  shiftId: string;
}

export interface AcceptShiftNominationResponse {
  value: string;
}

// Rate Practice API
/** @see Tempii.Application.Professionals.RatePractice.RatePracticeCommand */
export interface RatePracticeApiRequest {
  professionalId: string;
  practiceId: string;
  shiftApplicationId: string;
  rating: number;
  comment?: string;
}

/** Object, not a bare string — see `RateProfessionalResponse`. */
export interface RatePracticeResponse {
  value: string;
}

// Get Professional Ratings API
/** @see Tempii.Application.Professionals.GetProfessionalRatings.GetProfessionalRatingsQuery */
export interface GetProfessionalRatingsRequest {
  professionalId: string;
  page?: number;
  pageSize?: number;
}

/** Row in professional ratings list — mirrors `Tempii.Application.Payloads.RatingPayload`. */
export interface ProfessionalRatingItem {
  id: string;
  shiftApplicationId: string;
  raterId: string;
  raterName: string;
  ratedUserId: string;
  ratingValue: number;
  reviewText?: string;
  /**
   * Admin moderation state. Flagged ratings are still returned and still count towards
   * the average — only `isDeleted` rows are excluded, and those never reach the client.
   */
  isFlagged: boolean;
  createdAt: string;
}

export type GetProfessionalRatingsResponse = PaginatedResponse<ProfessionalRatingItem>;

// Settings Types — @see Tempii.Application.Professionals.GetSettings.ProfessionalSettingsResponse
export interface ProfessionalGeneralSettings {
  bio?: string;
  licenseNumber?: string;
  yearsOfExperience?: number;
  hourlyRateMinAmount?: number;
  hourlyRateMaxAmount?: number;
  hourlyRateCurrency?: string;
  maxTravelDistance: number;
  profilePhotoUrl?: string;
}

export interface ProfessionalNotificationSettings {
  emailNotifications: boolean;
  shiftAlerts: boolean;
  paymentAlerts: boolean;
  marketingEmails: boolean;
}

export interface ProfessionalBillingSettings {
  hasStripeAccount: boolean;
  stripeAccountStatus?: StripeAccountStatus;
  defaultCurrency?: string;
}

export interface ProfessionalSecuritySettings {
  email: string;
  verificationStatus: VerificationStatus;
}

/** @see Tempii.Application.Professionals.GetSettings.ProfessionalSettingsResponse */
export interface ProfessionalSettingsResponse {
  general: ProfessionalGeneralSettings;
  notifications: ProfessionalNotificationSettings;
  billing: ProfessionalBillingSettings;
  security: ProfessionalSecuritySettings;
}

/** @see Tempii.Application.Professionals.UpdateSettings.UpdateProfessionalSettingsRequest */
export interface UpdateProfessionalSettingsRequest {
  professionalId: string;
  emailNotifications?: boolean;
  shiftAlerts?: boolean;
  paymentAlerts?: boolean;
  marketingEmails?: boolean;
}

// Search Types
/** @see Tempii.Application.Professionals.SearchShifts.SearchShiftsQuery */
export interface SearchShiftsRequest {
  professionalId: string;
  searchTerm?: string;
  location?: string;
  dateFrom?: string;
  dateTo?: string;
  minHourlyRate?: number;
  maxHourlyRate?: number;
  page?: number;
  pageSize?: number;
}

// GET /professionals/{id}/recommended-shifts
/** @see Tempii.Application.Professionals.GetRecommendedShifts.GetRecommendedShiftsQuery */
export interface GetRecommendedShiftsRequest extends SearchShiftsRequest { }

/** Row in {@link SearchShiftsResponse} / {@link GetRecommendedShiftsResponse}. */
export interface SearchShiftItem {
  /** shifts.shifts.id — use for opportunity / apply routes. */
  shiftId: string;
  /** shifts.shift_posts.id — the dated row this search hit. */
  shiftPostId: string;
  title: string;
  description?: string;
  practiceName: string;
  practiceLogoUrl?: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  totalHours: number;
  hourlyRateAmount?: number;
  hourlyRateCurrency?: string;
  totalValueAmount: number;
  totalValueCurrency: string;
  status: string;
  candidatesCount: number;
  candidatesFulfilled: number;
  distanceKm?: number | null;
  matchScore?: number | null;
  recommendationSource?: string | null;
  scorerVersion?: string | null;
  generatedAt?: string | null;
}

/** @see Tempii.Application.Professionals.SearchShifts.SearchShiftsResponse */
export type SearchShiftsResponse = PaginatedResponse<SearchShiftItem>;
/** @see Tempii.Application.Professionals.GetRecommendedShifts.GetRecommendedShiftsResponse */
export type GetRecommendedShiftsResponse = PaginatedResponse<SearchShiftItem>;

/** @see Tempii.Application.Matching.RecordRecommendationEventRequest */
export interface RecordProfessionalRecommendationEventRequest {
  professionalId: string;
  shiftId: string;
  surface: "practice_shift" | "practice_feed" | "professional_feed";
  position: number;
  eventKind: "action";
  action: "click" | "dismiss" | "apply" | "nominate" | "open";
  variant?: "current" | "stale" | "fallback" | null;
  generationId?: string | null;
  idempotencyId?: string | null;
}

/**
 * Professional-facing shift statuses.
 * These represent the professional's perspective on their shift lifecycle.
 */
export type ProfessionalShiftStatus =
  | "applied"
  | "upcoming"
  | "active"
  | "completed"
  | "withdrawn";

/** Map API application status to display status for color coding */
export function getProfessionalShiftStatus(
  status: string
): ProfessionalShiftStatus {
  switch (status) {
    case "applied":
    case "nominated":
    case "draft":
      return "applied";
    case "selected":
      return "upcoming";
    case "withdrawn":
      return "withdrawn";
    default:
      return "applied";
  }
}

/**
 * Status colors for professional shift statuses.
 * Applied — Blue: waiting for response
 * Upcoming — Amber: accepted, shift approaching
 * Active — Green: currently working
 * Completed — Emerald: shift finished
 * Withdrawn — Slate: professional withdrew
 */
export const PROFESSIONAL_SHIFT_STATUS_COLORS: Record<
  ProfessionalShiftStatus,
  ShiftStatusColors
> = {
  applied: {
    bgLight: "#3B82F615",
    textLight: "#3B82F6",
    bgDark: "#60A5FA28",
    textDark: "#60A5FA",
    chipClasses:
      "bg-[#3B82F6]/10 text-[#3B82F6] dark:bg-[#60A5FA]/18 dark:text-[#60A5FA]",
    tabClasses:
      "!bg-[#3B82F6]/7 data-[state=active]:border-[#3B82F6] dark:!bg-[#60A5FA]/14 dark:data-[state=active]:!bg-[#60A5FA]/22 dark:data-[state=active]:border-[#60A5FA]",
    textClasses: "text-[#3B82F6] dark:text-[#60A5FA]",
    badgeClasses: "bg-[#3B82F6] dark:bg-[#3B82F6]",
    cardBorder: "border-l-4 border-l-blue-500",
  },
  upcoming: {
    bgLight: "#F59E0B15",
    textLight: "#F59E0B",
    bgDark: "#FBBF2428",
    textDark: "#FBBF24",
    chipClasses:
      "bg-[#F59E0B]/10 text-[#F59E0B] dark:bg-[#FBBF24]/18 dark:text-[#FBBF24]",
    tabClasses:
      "!bg-[#F59E0B]/7 data-[state=active]:border-[#F59E0B] dark:!bg-[#FBBF24]/14 dark:data-[state=active]:!bg-[#FBBF24]/22 dark:data-[state=active]:border-[#FBBF24]",
    textClasses: "text-[#F59E0B] dark:text-[#FBBF24]",
    badgeClasses: "bg-[#F59E0B] dark:bg-[#F59E0B]",
    cardBorder: "border-l-4 border-l-yellow-500",
  },
  active: {
    bgLight: "#10B98115",
    textLight: "#10B981",
    bgDark: "#34D39928",
    textDark: "#34D399",
    chipClasses:
      "bg-[#10B981]/10 text-[#10B981] dark:bg-[#34D399]/18 dark:text-[#34D399]",
    tabClasses:
      "!bg-[#10B981]/7 data-[state=active]:border-[#10B981] dark:!bg-[#34D399]/14 dark:data-[state=active]:!bg-[#34D399]/22 dark:data-[state=active]:border-[#34D399]",
    textClasses: "text-[#10B981] dark:text-[#34D399]",
    badgeClasses: "bg-[#10B981] dark:bg-[#10B981]",
    cardBorder: "border-l-4 border-l-green-500",
  },
  completed: {
    bgLight: "#05966915",
    textLight: "#059669",
    bgDark: "#2DD4BF28",
    textDark: "#2DD4BF",
    chipClasses:
      "bg-[#059669]/10 text-[#059669] dark:bg-[#2DD4BF]/18 dark:text-[#2DD4BF]",
    tabClasses:
      "!bg-[#059669]/7 data-[state=active]:border-[#059669] dark:!bg-[#2DD4BF]/14 dark:data-[state=active]:!bg-[#2DD4BF]/22 dark:data-[state=active]:border-[#2DD4BF]",
    textClasses: "text-[#059669] dark:text-[#2DD4BF]",
    badgeClasses: "bg-[#059669] dark:bg-[#059669]",
    cardBorder: "border-l-4 border-l-green-500",
  },
  withdrawn: {
    bgLight: "#64748B15",
    textLight: "#64748B",
    bgDark: "#94A3B828",
    textDark: "#94A3B8",
    chipClasses:
      "bg-[#64748B]/10 text-[#64748B] dark:bg-[#94A3B8]/18 dark:text-[#94A3B8]",
    tabClasses:
      "!bg-[#64748B]/7 data-[state=active]:border-[#64748B] dark:!bg-[#94A3B8]/14 dark:data-[state=active]:!bg-[#94A3B8]/22 dark:data-[state=active]:border-[#CBD5E1]",
    textClasses: "text-[#64748B] dark:text-[#CBD5E1]",
    badgeClasses: "bg-[#64748B] dark:bg-[#94A3B8]",
    cardBorder: "border-l-4 border-l-gray-500",
  }
};

/**
 * Icon component per professional shift status.
 */
export const PROFESSIONAL_SHIFT_STATUS_ICONS: Record<
  ProfessionalShiftStatus,
  React.ComponentType<{ className?: string }>
> = {
  applied: ClockIcon,
  upcoming: UrgentIcon,
  active: ActiveIcon,
  completed: CheckBoldIcon,
  withdrawn: CrossBoldIcon
};

/**
 * Display labels for professional shift statuses.
 */
export const PROFESSIONAL_SHIFT_STATUS_LABELS: Record<
  ProfessionalShiftStatus,
  string
> = {
  applied: "Applied",
  upcoming: "Upcoming",
  active: "Active",
  completed: "Completed",
  withdrawn: "Withdrawn"
};

// Map professional shift status to API ApplicationStatus values
export const STATUS_TO_API_STATUSES: Record<
  ProfessionalShiftStatus,
  ProfessionalApplicationStatus[]
> = {
  applied: ["applied", "nominated", "draft"],
  upcoming: ["selected"],
  active: ["selected"],
  completed: ["selected"],
  withdrawn: ["withdrawn"]
};

// Map generic tabs to temporal timeframe for server-side date filtering
export const STATUS_TO_TIMEFRAME: Partial<
  Record<ProfessionalShiftStatus, ShiftTimeframe>
> = {
  active: "today",
  completed: "past"
};

// Tabs that use the generic /shifts endpoint (no specific endpoint available)
export const GENERIC_TABS: ProfessionalShiftStatus[] = [
  "active",
  "completed",
  "withdrawn"
];

// Favourite Practices
/** @see Tempii.Application.Professionals.GetFavouritePractices.GetFavouritePracticesQuery */
export type GetFavouritePracticesRequest = string; // professionalId
/** @see Tempii.Application.Professionals.GetFavouritePractices.FavouritePracticeResponse */
export interface FavouritePracticeItem {
  practiceId: string;
  practiceName: string;
}
export type GetFavouritePracticesResponse = FavouritePracticeItem[];

/** @see Tempii.Application.Professionals.AddPracticeToFavourites.AddPracticeToFavouritesCommand */
export interface AddPracticeToFavouritesRequest {
  professionalId: string;
  practiceId: string;
}
export type AddPracticeToFavouritesResponse = string; // professionalId

/** @see Tempii.Application.Professionals.RemovePracticeFromFavourites.RemovePracticeFromFavouritesCommand */
export interface RemovePracticeFromFavouritesRequest {
  professionalId: string;
  practiceId: string;
}
export type RemovePracticeFromFavouritesResponse = string; // professionalId